import React from "react";

// One shape for every card on a link-grid page (Resources, Tools). `href` renders an anchor (external), `onSelect` a button (in-app) — so each card is a real control instead of a click-handling div.
export interface LinkCardItem {
  id: string;
  title: string;
  description: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  cta: string;
  /** Small pill beside the title, e.g. "Beta" */
  badge?: string;
  href?: string;
  onSelect?: () => void;
}

// Shared card face, so the cards can't drift apart — within a page or between the pages that use this grid
const LinkCardBody: React.FC<{ item: LinkCardItem }> = ({ item }) => (
  <>
    <div className="flex items-start gap-4">
      {/* Some icons hardcode their own stroke, so currentColor has to be forced in */}
      <item.Icon
        className="h-8 w-8 shrink-0 text-eai-hero-accent [&_path]:stroke-current"
        aria-hidden
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="eai-panel-title">{item.title}</h3>
          {item.badge && <span className="eai-tag-pill-sm">{item.badge}</span>}
        </div>
        <p className="eai-description-sm mt-1.5">{item.description}</p>
      </div>
    </div>
    <span className="mt-5 block text-right font-outfit text-eai-body-sm font-medium text-eai-cta-primary">
      {item.cta} →
    </span>
  </>
);

const CARD_CLASS =
  "eai-card-interactive flex h-full w-full min-w-0 flex-col justify-between p-6 text-left";

export const LinkCardGrid: React.FC<{ items: LinkCardItem[] }> = ({ items }) => (
  <div className="eai-card-grid">
    {items.map((item) =>
      item.href ? (
        <a
          key={item.id}
          href={item.href}
          // mailto: has no origin to open, so only the real link gets a new tab
          target={item.href.startsWith("mailto:") ? undefined : "_blank"}
          rel="noopener noreferrer"
          className={CARD_CLASS}
        >
          <LinkCardBody item={item} />
        </a>
      ) : (
        <button key={item.id} type="button" onClick={item.onSelect} className={CARD_CLASS}>
          <LinkCardBody item={item} />
        </button>
      )
    )}
  </div>
);

export default LinkCardGrid;
